import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Gift } from 'lucide-react'
import AdminToolNav from '../components/admin/AdminToolNav'
import {
  ONBOARDING_SETTLE_OUTCOMES,
  ONBOARDING_STEPS,
  type OnboardingStep,
  type OnboardingStepName,
  type OnboardingStepStatus,
  type OnboardingTarget,
  type OnboardingRecipientHistory,
  type OnboardingRunScope,
  type OnboardingRunTargetInput,
} from '../api/onboardingTypes'
import {
  useAdvanceOnboardingStep,
  useCancelOnboardingRun,
  useCreateOnboardingRun,
  useOnboardingCapabilities,
  useOnboardingEmailPreview,
  useOnboardingPreflight,
  useOnboardingRun,
  useRecoverOnboardingStep,
  useSendOnboardingTestEmail,
  useSettleOnboardingStep,
} from '../api/hooks'
import {
  claimedStepView,
  INPUT_CLASS,
  STATUS_TONE,
} from '../components/admin/onboarding/ui'
import DirectIntakePreview from '../components/admin/onboarding/DirectIntakePreview'
import { parseOnboardingEmails } from '../components/admin/onboarding/directIntakeLogic'
import RecipientHistory from '../components/admin/onboarding/RecipientHistory'
import RecipientHighlights from '../components/admin/onboarding/RecipientHighlights'
import { useRecipientHighlights } from '../components/admin/onboarding/useRecipientHighlights'
import ScopedBulkRun from '../components/admin/onboarding/ScopedBulkRun'
import BulkIntake from '../components/admin/onboarding/BulkIntake'
import BulkRunPanel from '../components/admin/onboarding/BulkRunPanel'
import EmailTemplateEditor from '../components/admin/onboarding/EmailTemplateEditor'
import TeamOwnerSummary from '../components/admin/onboarding/TeamOwnerSummary'
import TeamIntake, {
  NO_TEAM,
  teamChoiceFields,
  type TeamChoice,
} from '../components/admin/onboarding/TeamIntake'
import { isOnboardingDisabledError } from '../components/admin/onboarding/bulkLogic'
import { useAuth } from '../contexts/AuthContext'
import { hasTopupOperatorPermission } from '../components/admin/adminAccess'

// Re-exported so existing tests keep importing the page's helpers from here.
export { claimedStepView, minutesSince } from '../components/admin/onboarding/ui'

// White-glove onboarding operator surface (/admin/onboarding).
//
// ONE CLICK ADVANCES ONE STEP. There is no "run everything" control, and that
// is deliberate: creating an account, minting six figures of credit and
// mailing an external partner are three decisions, and a single button is how
// an operator makes all three by accident.
//
// The ordering rules shown here are a COURTESY, not the enforcement. The
// server decides what may run inside the step claim, so a disabled button is
// about making the right thing obvious rather than about safety.

type OnboardingMode = 'single' | 'bulk'
const ONBOARDING_MODES: readonly OnboardingMode[] = ['single', 'bulk']

/**
 * A step may be started only once EVERY earlier step has settled — not just the
 * previous one, which may be a team step created already skipped.
 *
 * Exported so the tests exercise THIS function rather than a copy of it — a
 * duplicated rule would keep passing while the page regressed. Takes the
 * narrowed `Pick` so a test can build a case from step+status alone.
 */
export function predecessorSettled(
  steps: Pick<OnboardingStep, 'step' | 'status'>[],
  step: OnboardingStepName,
): boolean {
  const index = ONBOARDING_STEPS.indexOf(step)
  return ONBOARDING_STEPS.slice(0, Math.max(index, 0)).every((earlier) => {
    const status = steps.find((candidate) => candidate.step === earlier)?.status
    return status === 'succeeded' || status === 'skipped'
  })
}

/**
 * Operator surface for white-glove partner onboarding.
 *
 * Creates a run, shows preflight, then advances one step per click.
 */
export default function AdminOnboardingPage() {
  const { t } = useTranslation('pages')
  const { user } = useAuth()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [credits, setCredits] = useState('')
  const [orgSlug, setOrgSlug] = useState('')
  const [orgDisplayName, setOrgDisplayName] = useState('')
  const [reason, setReason] = useState('')
  const [teamChoice, setTeamChoice] = useState<TeamChoice>(NO_TEAM)
  const postpaidChoice = teamChoice.mode !== 'none' && teamChoice.billingMode === 'postpaid'
  const [history, setHistory] = useState<OnboardingRecipientHistory | null>(null)
  const [emailFeedbackContainer, setEmailFeedbackContainer] = useState<HTMLDivElement | null>(null)
  const [scopes, setScopes] = useState<OnboardingRunScope[]>([])
  const [runId, setRunId] = useState<string | null>(null)
  // Single target, or a CSV roster (plan B2). Only chosen before a run exists.
  const [mode, setMode] = useState<OnboardingMode>('single')
  // Per-step opt-ins. Each defaults off because each widens blast radius:
  // a whole domain admits every future signup; an org move re-attributes
  // another customer's analytics.
  const [allowWholeDomain, setAllowWholeDomain] = useState(false)
  const [allowOrgMove, setAllowOrgMove] = useState(false)
  const [creditsConfirmed, setCreditsConfirmed] = useState(false)
  // Deliberate re-onboarding of an address that already has a run. Off by
  // default: two runs for one partner is almost always a mistake.
  const [acknowledgementScope, setAcknowledgementScope] = useState<string | null>(null)
  // Which target the operator has test-sent to themselves, and where it went.
  // Keyed by target so a test for one recipient never unlocks another's send.
  const [testSent, setTestSent] = useState<{ targetId: string; sentTo: string } | null>(null)

  useEffect(() => {
    setAcknowledgementScope(null)
  }, [email, firstName, credits, orgSlug, orgDisplayName, reason, teamChoice])

  useEffect(() => {
    if (postpaidChoice && credits !== '0') setCredits('0')
  }, [credits, postpaidChoice])

  const selectedScope = scopes.find((scope) => scope.run_id === runId)
  const scopeIds = selectedScope?.target_ids
  const createRun = useCreateOnboardingRun()
  const advance = useAdvanceOnboardingStep(scopeIds)
  const settle = useSettleOnboardingStep(scopeIds)
  const cancel = useCancelOnboardingRun()
  const testSend = useSendOnboardingTestEmail(scopeIds)
  const recover = useRecoverOnboardingStep(scopeIds)
  const { data: run, error: runError } = useOnboardingRun(runId, scopeIds)
  const { data: preflight, error: preflightError } = useOnboardingPreflight(runId, scopeIds)
  // P3 §6: an environment with the tool switched off says so, instead of every
  // request failing with a bare 404. Capabilities answers even when it is off.
  const { data: capabilities } = useOnboardingCapabilities()

  const creditsValue = Number.parseInt(credits, 10)
  const creditsAmount = postpaidChoice ? 0
    : Number.isFinite(creditsValue) && creditsValue > 0 ? creditsValue : 0

  const emailList = useMemo(() => parseOnboardingEmails(email), [email])
  const multiple = email.includes(',')
  const firstEmail = email.split(',')[0].trim().toLowerCase()
  const trialTarget = run?.targets.length === 1 ? run.targets[0] : null
  const trialHref = trialTarget?.wallet_kind === 'team' && trialTarget.credited_team_id
    ? `/admin/free-trials?team_id=${encodeURIComponent(trialTarget.credited_team_id)}`
    : trialTarget?.wallet_kind === 'personal' && trialTarget.email
      ? `/admin/free-trials?email=${encodeURIComponent(trialTarget.email)}`
      : null
  const teamFields = useMemo(() => {
    if (
      teamChoice.mode === 'personal' &&
      (multiple || teamChoice.personalPolicyEmail === null || teamChoice.personalPolicyEmail !== firstEmail)
    ) return null
    return teamChoiceFields(teamChoice)
  }, [teamChoice, firstEmail, multiple])
  const inputsValid = useMemo(() => {
    const emailOk = emailList.error === null
    const creditsOk = credits.trim() === '' || (/^[0-9]+$/.test(credits.trim()) && Number.isSafeInteger(Number(credits)) && Number(credits) >= 0)
    return emailOk && creditsOk && reason.trim().length > 0
  }, [emailList, credits, reason])
  const currentHistory = history?.email === email.trim().toLowerCase() ? history : null
  const recipientHighlights = useRecipientHighlights(mode === 'single' && !runId ? email : '', history)
  const historyScope = currentHistory ? JSON.stringify([email, firstName, credits, orgSlug, orgDisplayName, reason, teamChoice, currentHistory.history.map((entry) => entry.target_id)]) : null
  const allowRepeat = historyScope !== null && acknowledgementScope === historyScope
  const canStart = inputsValid && teamFields !== null && currentHistory !== null && (
    currentHistory.classification === 'new' ||
    (currentHistory.classification === 'requires_acknowledgement' && allowRepeat)
  )

  const directTargets: OnboardingRunTargetInput[] = useMemo(() => emailList.emails.map((address, index) => ({
    email: address, first_name: index === 0 ? firstName.trim() || null : null,
    credits: creditsAmount, org_slug: orgSlug.trim() || null,
    org_display_name: orgDisplayName.trim() || null, ...teamFields,
  })), [emailList, firstName, creditsAmount, orgSlug, orgDisplayName, teamFields])
  const directInputKey = JSON.stringify([email, firstName, credits, orgSlug, orgDisplayName, reason, teamChoice])

  const selectedRun = run && selectedScope ? { ...run, targets: run.targets.filter((target) => selectedScope.target_ids.includes(target.target_id)) } : run
  const target: OnboardingTarget | undefined = selectedRun?.targets[0]
  // The mode is fixed once a run exists (the toggle is shown only before), so a
  // run created from CSV stays in the bulk panel however many rows it has.
  const showBulkPanel = run !== undefined && (selectedRun && selectedRun.targets.length > 1 || scopes.length > 1 || mode === 'bulk')
  const targetPreflight = preflight?.targets.find((report) => report.target_id === target?.target_id)

  // Two independent hard stops, both reported by the server. The trigger one
  // is environment-wide; the other is specific to this address.
  const blockingReason =
    (preflight && !preflight.trigger_enforcing ? preflight.trigger_blocking_reason : null) ??
    targetPreflight?.blocking_reason ??
    null

  const wholeRunSelected = !selectedScope && run?.scope !== 'targets'
  const runFinished = run?.status === 'cancelled' || run?.status === 'completed'

  const isStepReady = (steps: OnboardingStep[], stepName: OnboardingStepName): boolean => {
    const status = steps.find((candidate) => candidate.step === stepName)?.status ?? 'pending'
    return (
      predecessorSettled(steps, stepName) &&
      (stepName !== 'team' || target?.team_dependency_ready !== false) &&
      !runFinished &&
      !blockingReason &&
      (status === 'pending' || status === 'failed')
    )
  }

  // Fetched only once email is the next step, so the copy is reviewed at the
  // moment it is about to go out rather than at run creation.
  const emailReady = target ? isStepReady(target.steps, 'email') : false
  const emailPreview = useOnboardingEmailPreview(runId, target?.target_id ?? null, emailReady)
  // The address is held, not just a flag, so the confirmation names where it went.
  const testSentTo = testSent && testSent.targetId === target?.target_id ? testSent.sentTo : null
  // The SERVER's attestation is the only unlock: it survives a reload and goes
  // stale when the template changes. The local result is delivery feedback only
  // ("sent to you@…"), never a reason to offer a send the server will refuse.
  const emailTestSent = run?.email_test_current === true

  const handleCreate = async () => {
    if (!canStart || teamFields === null) return
    try {
      const created = await createRun.mutateAsync({
        reason: reason.trim(),
        targets: [
          {
            email: email.trim().toLowerCase(),
            first_name: firstName.trim() || null,
            credits: creditsAmount,
            org_slug: orgSlug.trim() || null,
            org_display_name: orgDisplayName.trim() || null,
            ...teamFields,
          },
        ],
        acknowledgedPriorTargets: allowRepeat && currentHistory?.selected
          ? { [currentHistory.email]: currentHistory.selected.target_id } : {},
      })
      setScopes(created.scope === 'targets' ? [{ run_id: created.run_id, target_ids: created.scope_target_ids ?? created.targets.map((target) => target.target_id) }] : [])
      setRunId(created.run_id)
    } catch {
      // Creation rechecks current history; a raced rejection invalidates eligibility.
      setHistory(null)
      setAcknowledgementScope(null)
      // Surfaced through createRun.error in the action banner.
    }
  }

  const handleRecover = (stepName: OnboardingStepName) => {
    if (!runId || !target) return
    recover.mutate({ runId, targetId: target.target_id, step: stepName })
  }

  const handleAdvance = (step: OnboardingStepName) => {
    if (!runId || !target) return
    advance.mutate({
      runId,
      targetId: target.target_id,
      step,
      options: { allowWholeDomain, allowOrgMove },
    })
  }

  const handleTestSend = () => {
    if (!runId || !target) return
    const targetId = target.target_id
    testSend.mutate(
      { runId, targetId },
      { onSuccess: (result) => setTestSent({ targetId, sentTo: result.sent_to }) },
    )
  }

  // instanceof rather than a cast: react-query types these as unknown, and
  // asserting Error would render "undefined" for anything thrown that is not one.
  // `cancel` is in the chain too: a rejected cancel (409 on an already-terminal
  // run) would otherwise fail silently, leaving the operator unable to tell
  // whether the remaining steps are actually stopped.
  const rawError =
    createRun.error ??
    advance.error ??
    settle.error ??
    cancel.error ??
    testSend.error ??
    recover.error ??
    runError ??
    preflightError
  const previewError = emailPreview.error instanceof Error ? emailPreview.error.message : null
  const toolOff =
    capabilities?.onboarding_enabled === false ||
    isOnboardingDisabledError(rawError) ||
    isOnboardingDisabledError(emailPreview.error)
  const actionErrorMessage = rawError instanceof Error && !toolOff ? rawError.message : null

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" data-testid="admin-onboarding-page">
      <AdminToolNav />
      {/* The bulk panel's per-step columns need the page's full width. */}
      <div className={showBulkPanel ? '' : 'max-w-2xl'}>
        <h1 className="mb-1 text-xl font-semibold text-foreground">{t('adminOnboarding.title')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t('adminOnboarding.description')}</p>
        {trialHref && hasTopupOperatorPermission(user) && (
          <Link
            to={trialHref}
            className="mb-5 inline-flex h-9 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
          >
            <Gift className="h-4 w-4" />
            {t('adminFreeTrials.title')}
          </Link>
        )}

        {toolOff && (
          <div
            className="mb-4 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-warning"
            data-testid="onboarding-tool-off"
          >
            {t('adminOnboarding.toolOff')}
          </div>
        )}

        {!run && !toolOff && (
          <div className="mb-4 flex gap-1" role="tablist" data-testid="onboarding-mode">
            {ONBOARDING_MODES.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                onClick={() => setMode(value)}
                className={`h-8 rounded-lg px-3 text-sm ${
                  mode === value
                    ? 'bg-surface-3 text-foreground'
                    : 'text-muted-foreground hover:bg-surface-2'
                }`}
                data-testid={`onboarding-mode-${value}`}
              >
                {t(`adminOnboarding.mode.${value}`)}
              </button>
            ))}
          </div>
        )}

        {!run && !toolOff && mode === 'bulk' && <BulkIntake onCreated={(createdScopes) => { setScopes(createdScopes); setRunId(createdScopes[0]?.run_id ?? null) }} />}

        {!run && !toolOff && mode === 'single' && (
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface-1 p-5">
            <div className="flex flex-col gap-1.5">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-foreground">{t('adminOnboarding.emailLabel')}</span>
                <input
                  type="text"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setAcknowledgementScope(null); setHistory(null); createRun.reset() }}
                  placeholder={t('adminOnboarding.emailPlaceholder')}
                  className={INPUT_CLASS}
                  data-testid="onboarding-email-input"
                />
                <span className="text-xs text-muted-foreground">{t('adminOnboarding.direct.emailHelp')}</span>
                {email !== '' && emailList.error && <span role="alert" className={`text-xs ${emailList.error.key === 'duplicate' ? 'text-warning' : 'text-destructive'}`} data-testid="onboarding-email-list-error">
                  {emailList.error.key === 'duplicate' ? <Trans ns="pages" i18nKey="adminOnboarding.direct.error.duplicate"
                    values={{ email: email.split(',')[emailList.error.index - 1].trim().toLowerCase() }}
                    components={{ email: <span className="rounded bg-warning/10 px-1 font-medium" data-testid="onboarding-repeated-email" /> }} />
                    : t(`adminOnboarding.direct.error.${emailList.error.key}`, { index: emailList.error.index })}
                </span>}
              </label>
              <div ref={setEmailFeedbackContainer} className="flex flex-col gap-3" data-testid="onboarding-email-feedback">
                {multiple && <RecipientHighlights histories={recipientHighlights.histories} unavailable={recipientHighlights.unavailable} />}
                {!(multiple || emailList.error) && <RecipientHistory email={email} history={currentHistory} onHistory={setHistory}
                  acknowledged={allowRepeat} onAcknowledged={(checked) => setAcknowledgementScope(checked ? historyScope : null)} />}
                {!multiple && createRun.error instanceof Error && <p role="alert" className="text-sm text-destructive" data-testid="onboarding-intake-error">{createRun.error.message}</p>}
              </div>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">{t('adminOnboarding.firstNameLabel')}</span>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={INPUT_CLASS}
                data-testid="onboarding-first-name-input"
              />
              {multiple && <span className="text-xs text-muted-foreground" data-testid="onboarding-first-name-help">{t('adminOnboarding.direct.firstNameHelp')}</span>}
            </label>

            <TeamIntake value={teamChoice} onChange={setTeamChoice} partnerEmail={firstEmail} />
            {teamChoice.mode === 'create' && <TeamOwnerSummary email={firstEmail || t('adminOnboarding.direct.firstEmail')} />}

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">{t('adminOnboarding.creditsLabel')}</span>
              <input
                type="number"
                min={0}
                value={credits}
                disabled={postpaidChoice}
                onChange={(e) => setCredits(e.target.value)}
                className={INPUT_CLASS}
                data-testid="onboarding-credits-input"
              />
              <span className="text-xs font-medium text-muted-foreground">
                {postpaidChoice ? t('adminOnboarding.team.postpaidCreditsHelp') : t('adminOnboarding.creditsHelp')}
                {multiple && <span className="block" data-testid="onboarding-credits-total">{t('adminOnboarding.direct.creditsTotal', { amount: creditsAmount, count: emailList.emails.length, total: creditsAmount * emailList.emails.length })}</span>}
              </span>
              {teamChoice.mode !== 'none' && (
                <span
                  className="text-xs font-medium text-muted-foreground"
                  data-testid="onboarding-credits-team-wallet"
                >
                  {t('adminOnboarding.team.creditsToTeam')}
                  {multiple && <span className="block">{t('adminOnboarding.direct.sharedPool')}</span>}
                </span>
              )}
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">{t('adminOnboarding.orgLabel')}</span>
              <input
                type="text"
                value={orgSlug}
                onChange={(e) => setOrgSlug(e.target.value)}
                placeholder={t('adminOnboarding.orgPlaceholder')}
                className={INPUT_CLASS}
                data-testid="onboarding-org-input"
              />
              <span className="text-xs font-medium text-muted-foreground">{t('adminOnboarding.orgHelp')} {multiple && t('adminOnboarding.direct.orgHelp')}</span>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">
                {t('adminOnboarding.orgDisplayNameLabel')}
              </span>
              <input
                type="text"
                value={orgDisplayName}
                onChange={(e) => setOrgDisplayName(e.target.value)}
                className={INPUT_CLASS}
                data-testid="onboarding-org-display-name-input"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">{t('adminOnboarding.reasonLabel')}</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('adminOnboarding.reasonPlaceholder')}
                className={INPUT_CLASS}
                data-testid="onboarding-reason-input"
              />
            </label>

            {multiple ? (inputsValid && teamFields !== null ? <DirectIntakePreview key={directInputKey}
              targets={directTargets} reason={reason.trim()} sharedTeam={teamChoice.mode === 'create'}
              feedbackContainer={emailFeedbackContainer}
              onCreated={(createdScopes) => { setScopes(createdScopes); setRunId(createdScopes[0]?.run_id ?? null) }} />
              : <button type="button" disabled className="h-9 rounded-lg bg-primary/40 text-sm text-primary-foreground" data-testid="onboarding-start-button">{t('adminOnboarding.startButton')}</button>) : <>
            <button
              type="button"
              disabled={!canStart || createRun.isPending}
              onClick={() => void handleCreate()}
              className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:bg-primary/40"
              data-testid="onboarding-start-button"
            >
              {createRun.isPending
                ? t('adminOnboarding.starting')
                : t('adminOnboarding.startButton')}
            </button>
            </>}
            {!inputsValid && (
              <p className="text-xs font-medium text-muted-foreground" data-testid="onboarding-inputs-hint">
                {t('adminOnboarding.inputsHint')}
              </p>
            )}
            {inputsValid && teamFields === null && (
              <p className="text-xs font-medium text-muted-foreground" data-testid="onboarding-team-hint">
                {t('adminOnboarding.team.incomplete')}
              </p>
            )}
          </div>
        )}

        {/* A CSV run — even one row — or any multi-recipient run gets the bulk
            panel, with its per-step confirmations. A typed single-partner run
            keeps the step ladder below, unchanged. */}
        {run && showBulkPanel && (scopes.length ? scopes.map((scope) => <ScopedBulkRun key={scope.run_id} scope={scope} />) : <BulkRunPanel run={run} preflight={preflight} />)}

        {blockingReason && run && !showBulkPanel && (
          <div
            className="mb-4 mt-6 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            data-testid="onboarding-blocking-reason"
          >
            {blockingReason}
          </div>
        )}

        {actionErrorMessage && (runId !== null || mode !== 'single') && (
          <div
            className="mb-4 mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            data-testid="onboarding-action-error"
          >
            {actionErrorMessage}
          </div>
        )}

        {run && target && !showBulkPanel && (
          <>
            <div className="mt-6 flex items-center gap-3">
              <h2 className="text-sm font-semibold text-foreground">
                {t('adminOnboarding.stepsTitle')}
              </h2>
              <span className="text-xs font-medium text-muted-foreground" data-testid="onboarding-run-summary">
                {t('adminOnboarding.runSummary', {
                  email: target.email ?? t('adminOnboarding.purgedRecipient'),
                  status: t(`adminOnboarding.runStatus.${run.status}`),
                })}
              </span>
              {!runFinished && wholeRunSelected && (
                <button
                  type="button"
                  onClick={() => void cancel.mutate({ runId: run.run_id })}
                  className="ml-auto h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:bg-surface-3"
                  data-testid="onboarding-cancel-button"
                >
                  {t('adminOnboarding.cancelRun')}
                </button>
              )}
            </div>
            {/* Cancelling stops further steps; it cannot undo an account, a
                grant or a sent email. Said plainly so nobody expects a rollback. */}
            <p className="mb-3 mt-1 text-xs font-medium text-muted-foreground">
              {t('adminOnboarding.cancelHelp')}
            </p>

            <ol className="flex flex-col gap-2" data-testid="onboarding-step-list">
              {ONBOARDING_STEPS.map((stepName, index) => {
                const step = target.steps.find((candidate) => candidate.step === stepName)
                const status: OnboardingStepStatus = step?.status ?? 'pending'
                const ready = isStepReady(target.steps, stepName)
                const isCredits = stepName === 'credits'
                // From the PERSISTED target, not the form: a run created from a
                // one-row CSV leaves the form empty but still grants credits.
                const needsCreditConfirmation = isCredits && target.credits > 0 && !creditsConfirmed
                // The real send waits for a test the operator has received
                // themselves. A courtesy gate like the others — but the send is
                // the one step that cannot be recalled.
                const needsTestSend = stepName === 'email' && !emailTestSent

                return (
                  <li
                    key={stepName}
                    className="rounded-lg border border-border bg-surface-1 px-3 py-2.5"
                    data-testid={`onboarding-step-${stepName}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* 1-indexed: operators read "step 3 of 7", not an offset. */}
                      <span className="w-4 text-xs font-medium text-muted-foreground">{index + 1}</span>
                      <span className="flex-1 text-sm text-foreground">
                        {t(`adminOnboarding.step.${stepName}`)}
                      </span>
                      <span
                        className={`rounded border px-1.5 py-0.5 text-xs ${STATUS_TONE[status]}`}
                        data-testid={`onboarding-status-${stepName}`}
                      >
                        {t(`adminOnboarding.status.${status}`)}
                      </span>
                      {ready && (
                        <button
                          type="button"
                          disabled={advance.isPending || needsCreditConfirmation || needsTestSend}
                          onClick={() => handleAdvance(stepName)}
                          className="h-7 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground disabled:cursor-not-allowed disabled:bg-primary/40"
                          data-testid={`onboarding-run-${stepName}`}
                        >
                          {t('adminOnboarding.runStep')}
                        </button>
                      )}
                    </div>

                    {stepName === 'team' && target.team_mode && (
                      <p
                        className="mt-1.5 pl-7 text-xs font-medium text-muted-foreground"
                        data-testid="onboarding-team-summary"
                      >
                        {target.team_mode === 'group_member'
                          ? t('adminOnboarding.direct.memberSummary', { name: target.team_name ?? '', owner: target.team_owner_target_id ?? '' })
                          : target.team_mode === 'create'
                          ? t('adminOnboarding.team.summary.create', { name: target.team_name ?? '' })
                          : t('adminOnboarding.team.summary.join', {
                              name: target.team_name ?? target.team_id ?? '',
                              role: t(`adminOnboarding.team.role.${target.team_role ?? 'member'}`),
                            })}
                      </p>
                    )}
                    {stepName === 'team' && target.team_mode && <p className="mt-1.5 pl-7 text-xs text-muted-foreground" data-testid="onboarding-billing-summary">
                      {t(target.credit_billing_mode === 'postpaid' ? 'adminOnboarding.team.billingSummaryPostpaid' : 'adminOnboarding.team.billingSummaryPrepaid', {
                        mode: t(`adminOnboarding.team.billingMode.${target.credit_billing_mode}`),
                        months: target.billing_period_months ?? '', timezone: target.billing_timezone ?? '',
                      })}
                    </p>}

                    {stepName === 'team' && target.team_dependency_ready === false && <p className="mt-1.5 pl-7 text-xs text-warning" data-testid="onboarding-team-dependency">{t('adminOnboarding.direct.ownerFirst', { owner: target.team_owner_target_id })}</p>}

                    {step?.error_message && (
                      <p
                        className="mt-1.5 pl-7 text-xs font-medium text-destructive"
                        data-testid={`onboarding-error-${stepName}`}
                      >
                        {step.error_code}: {step.error_message}
                      </p>
                    )}

                    {/* A claimed step is either still running (fresh heartbeat)
                        or was interrupted. Recovery is offered only after a
                        while, and the server refuses it unless the step's
                        executor is verifiably gone. */}
                    {(status === 'claimed' || status === 'running') && (
                      <ClaimedStepNotice
                        view={claimedStepView(step?.claimed_at, step?.heartbeat_at)}
                        pending={recover.isPending}
                        onRecover={() => handleRecover(stepName)}
                        testId={stepName}
                      />
                    )}

                    {/* Review, then test, then send. Neither the preview nor the
                        test send is a step: the server claims nothing for them,
                        so they can be repeated freely. Both use a PLACEHOLDER
                        password, so neither can leak the partner's credential.
                        (This replaces a "dry run" that advanced the step and so
                        closed it with nothing sent.) */}
                    {stepName === 'email' && !runFinished && wholeRunSelected && (
                      <div className="mt-2 pl-7">
                        <EmailTemplateEditor runId={run.run_id} previewTargetId={target.target_id} />
                      </div>
                    )}

                    {stepName === 'email' && ready && (
                      <div className="mt-2 pl-7" data-testid="onboarding-email-preview">
                        <p className="text-xs font-medium text-muted-foreground">
                          {t('adminOnboarding.emailPreview.intro')}
                        </p>
                        {emailPreview.isLoading && (
                          <p className="mt-1 text-xs font-medium text-muted-foreground">
                            {t('adminOnboarding.emailPreview.loading')}
                          </p>
                        )}
                        {previewError && (
                          <p
                            className="mt-1 text-xs font-medium text-destructive"
                            data-testid="onboarding-email-preview-error"
                          >
                            {previewError}
                          </p>
                        )}
                        {emailPreview.data && (
                          <>
                            {emailPreview.data.warnings.map((warning) => (
                              <p
                                key={warning}
                                className="mt-1 text-xs font-medium text-warning"
                                data-testid="onboarding-email-warning"
                              >
                                {warning}
                              </p>
                            ))}
                            <p className="mt-2 text-xs font-medium text-foreground">
                              <span className="text-muted-foreground">
                                {t('adminOnboarding.emailPreview.subject')}
                              </span>{' '}
                              {emailPreview.data.subject}
                            </p>
                            {/* sandbox="" is the most restrictive setting: no
                                scripts, no same-origin, no forms, no navigation.
                                The body is server-rendered from a fixed template
                                with the name escaped, but the frame must not
                                depend on that staying true. */}
                            <iframe
                              title={t('adminOnboarding.emailPreview.frameTitle')}
                              sandbox=""
                              referrerPolicy="no-referrer"
                              srcDoc={emailPreview.data.html}
                              className="mt-1 h-96 w-full rounded border border-border bg-white"
                              data-testid="onboarding-email-preview-frame"
                            />
                            <details className="mt-1 text-xs font-medium text-muted-foreground">
                              <summary className="cursor-pointer">
                                {t('adminOnboarding.emailPreview.plainText')}
                              </summary>
                              <pre
                                className="mt-1 whitespace-pre-wrap rounded border border-border bg-surface-2 p-2 text-foreground"
                                data-testid="onboarding-email-preview-text"
                              >
                                {emailPreview.data.text}
                              </pre>
                            </details>
                          </>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            disabled={testSend.isPending || !emailPreview.data}
                            onClick={handleTestSend}
                            className="h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid="onboarding-email-test-send"
                          >
                            {t('adminOnboarding.emailTestSend')}
                          </button>
                          {testSentTo && (
                            <span
                              className="text-xs font-medium text-success"
                              data-testid="onboarding-email-test-sent"
                            >
                              {t('adminOnboarding.emailTestSent', { address: testSentTo })}
                            </span>
                          )}
                        </div>
                        {!emailTestSent && (
                          <p className="mt-1 text-xs font-medium text-muted-foreground">
                            {t('adminOnboarding.emailSendNeedsTest')}
                          </p>
                        )}
                      </div>
                    )}

                    {needsCreditConfirmation && (
                      <label className="mt-2 flex items-center gap-2 pl-7 text-xs font-medium text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={creditsConfirmed}
                          onChange={(e) => setCreditsConfirmed(e.target.checked)}
                          data-testid="onboarding-credits-confirm"
                        />
                        {t('adminOnboarding.creditsConfirm', { amount: target.credits })}
                      </label>
                    )}

                    {stepName === 'allowlist' && ready && (
                      <label className="mt-2 flex items-center gap-2 pl-7 text-xs font-medium text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={allowWholeDomain}
                          onChange={(e) => setAllowWholeDomain(e.target.checked)}
                          data-testid="onboarding-allow-whole-domain"
                        />
                        {t('adminOnboarding.allowWholeDomain')}
                      </label>
                    )}

                    {stepName === 'org' && ready && (
                      <label className="mt-2 flex items-center gap-2 pl-7 text-xs font-medium text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={allowOrgMove}
                          onChange={(e) => setAllowOrgMove(e.target.checked)}
                          data-testid="onboarding-allow-org-move"
                        />
                        {t('adminOnboarding.allowOrgMove')}
                      </label>
                    )}

                    {/* delivery_unknown is terminal: the mail may or may not
                        have gone. Only an operator who has checked SES can say,
                        and 'not delivered' re-opens the step for a resend with
                        a FRESH password. */}
                    {status === 'delivery_unknown' && (
                      <div className="mt-2 flex items-center gap-2 pl-7">
                        <span className="text-xs font-medium text-warning">
                          {t('adminOnboarding.settlePrompt')}
                        </span>
                        {ONBOARDING_SETTLE_OUTCOMES.map((outcome) => (
                          <button
                            key={outcome}
                            type="button"
                            // Settling is one-way: 'delivered' closes the step.
                            // Without this guard an operator can land both
                            // outcomes before the first returns, and the second
                            // comes back NOT_SETTLEABLE — which reads as "the
                            // resend failed" rather than "you already settled".
                            disabled={settle.isPending}
                            onClick={() =>
                              settle.mutate({
                                runId: run.run_id,
                                targetId: target.target_id,
                                step: stepName,
                                outcome,
                              })
                            }
                            className="h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:bg-surface-3"
                            data-testid={`onboarding-settle-${outcome}`}
                          >
                            {t(`adminOnboarding.settle.${outcome}`)}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          </>
        )}
      </div>
    </div>
  )
}


function ClaimedStepNotice({
  view,
  pending,
  onRecover,
  testId,
}: {
  view: ReturnType<typeof claimedStepView>
  pending: boolean
  onRecover: () => void
  testId: string
}) {
  const { t } = useTranslation('pages')
  const { claimedMinutes, interrupted, canOfferRecover } = view
  return (
    <div
      className="mt-2 flex items-center gap-2 pl-7 text-xs"
      data-testid={`onboarding-claimed-${testId}`}
    >
      <span className={interrupted ? 'text-warning' : 'text-muted-foreground'}>
        {interrupted
          ? t('adminOnboarding.claimedInterrupted')
          : t('adminOnboarding.claimedRunning', { minutes: claimedMinutes ?? 0 })}
      </span>
      {canOfferRecover && (
        <button
          type="button"
          disabled={pending}
          onClick={onRecover}
          className="h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs text-foreground hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`onboarding-recover-${testId}`}
        >
          {t('adminOnboarding.recover')}
        </button>
      )}
    </div>
  )
}
